/**
 * SIL-2218 - the buyer's deposit area, shared by the dual-profile /offers table
 * (BuyerOfferActions) and the buyer-only My Activity offer cards. Moved out of
 * routes/offers.tsx unchanged so there is ONE copy of the escrow logic.
 *
 * The server is the gate (isOwnOffer, buyer-org-only escrow routes); callers
 * render this only for the viewer's own offers.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, throwIfNotOk } from '../../../lib/api';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { SafeLink } from '@siliconx/shared/ui/SafeLink';
import type { Offer } from '@siliconx/shared/dto/offer';
import type {
  EscrowDepositView,
  OfferDepositQuote,
  PayDepositResponse,
} from '@siliconx/shared/dto/offer-payment';
import type { PaymentMethodDetail } from '@siliconx/shared/dto/payment-method';
import { qk } from '../../../lib/queryKeys';
import { useAuth } from '../../../lib/authContext';
import { EscrowAgentDisclosure } from '../EscrowAgentDisclosure';
import { createPurchaseTermsConsent, isPurchaseTermsConsentRequiredError } from '../../../lib/purchaseTerms';
import {
  PURCHASE_TERMS_BODY,
  PURCHASE_TERMS_CHECKBOX_LABEL,
  PURCHASE_TERMS_CONSENT_ERROR,
  PURCHASE_TERMS_HEADING,
} from '../copy/purchaseTerms';

// SIL-1598: the buyer's balance pay link. Same flow, API and consent gate as the deposit: the live balance
// comes back from GET /:id/escrow once one exists (kind 'balance'); until then (deposit only) nothing renders.
export function BalanceAction({ offer }: { offer: Offer }) {
  const { data, isLoading, refetch, isFetching } = useQuery<{ escrow: EscrowDepositView | null }>({
    queryKey: [...qk.offers(), offer.id, 'escrow'],
    queryFn: async () => {
      const res = await api.api.client.offers[':id'].escrow.$get({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { escrow: EscrowDepositView | null };
    },
    gcTime: 0,
    refetchInterval: (q) => (q.state.data?.escrow?.status === 'pending_create' ? 15_000 : false),
  });
  if (isLoading) return null;
  const escrow = data?.escrow;
  if (!escrow || escrow.kind !== 'balance') return null;
  return (
    <div className="flex flex-col gap-2 rounded border border-teal-300 bg-teal-50 p-2 text-xs" data-testid="escrow-balance">
      <div>
        Balance due: <UsdCell value={escrow.amountUsd} /> (the agreed price of <UsdCell value={offer.amountUsd} /> minus your
        deposit), held by Escrow.com
      </div>
      {escrow.error === 'consent_required' ? (
        <EscrowConsentStep offerId={offer.id} onRecorded={() => void refetch()} />
      ) : escrow.payUrl ? (
        <SafeLink
          href={escrow.payUrl}
          fallback="Payment link unavailable"
          className="w-fit rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100"
        >
          Pay balance on Escrow.com
        </SafeLink>
      ) : escrow.error ? (
        <div className="flex items-center gap-2 text-zinc-700">
          <span>We could not load your Escrow.com payment link.</span>
          <button
            type="button"
            disabled={isFetching}
            onClick={() => void refetch()}
            className="rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100"
          >
            Try again
          </button>
        </div>
      ) : escrow.status === 'open' ? (
        <span className="text-zinc-600">Your payment link is ready.</span>
      ) : (
        <span className="text-zinc-600">Preparing your Escrow.com payment link. This page updates automatically.</span>
      )}
      <EscrowAgentDisclosure />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SIL-1593: business-org offers pay their deposit on Escrow.com. When the
// offer has a live Escrow.com deposit transaction, show its pay link INSTEAD
// of the mock deposit flow; otherwise fall through to the mock flow below.
// The link only ever comes from the API (https escrow.com host, buyer org only).
// ---------------------------------------------------------------------------
export function DepositAction({ offer }: { offer: Offer }) {
  const { data, isLoading, refetch, isFetching } = useQuery<{ escrow: EscrowDepositView | null }>({
    queryKey: [...qk.offers(), offer.id, 'escrow'],
    queryFn: async () => {
      const res = await api.api.client.offers[':id'].escrow.$get({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { escrow: EscrowDepositView | null };
    },
    // The pay link is a buyer credential: never keep it in the cache beyond this view
    // (gcTime 0), never persist it. Poll only while the transaction is still being
    // created; after an Escrow.com error stop and offer a manual retry.
    gcTime: 0,
    refetchInterval: (q) => (q.state.data?.escrow?.status === 'pending_create' ? 15_000 : false),
  });
  if (isLoading) return null;
  const escrow = data?.escrow;
  if (!escrow) return <PayDepositAction offer={offer} />;
  return (
    <div className="flex flex-col gap-2 rounded border border-teal-300 bg-teal-50 p-2 text-xs" data-testid="escrow-deposit">
      <div>
        Deposit: <UsdCell value={escrow.amountUsd} /> (toward the <UsdCell value={offer.amountUsd} /> price), held by
        Escrow.com
      </div>
      {escrow.error === 'consent_required' ? (
        <EscrowConsentStep offerId={offer.id} onRecorded={() => void refetch()} />
      ) : escrow.payUrl ? (
        <SafeLink
          href={escrow.payUrl}
          fallback="Payment link unavailable"
          className="w-fit rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100"
        >
          Pay deposit on Escrow.com
        </SafeLink>
      ) : escrow.error ? (
        <div className="flex items-center gap-2 text-zinc-700">
          <span>We could not load your Escrow.com payment link.</span>
          <button
            type="button"
            disabled={isFetching}
            onClick={() => void refetch()}
            className="rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100"
          >
            Try again
          </button>
        </div>
      ) : (
        <span className="text-zinc-600">Preparing your Escrow.com payment link. This page updates automatically.</span>
      )}
      <EscrowAgentDisclosure />
    </div>
  );
}

// SIL-1594: the Escrow.com pay link is only returned once the buyer has recorded the
// same offer_deposit purchase-terms consent the mock deposit requires (SIL-1364). Until
// then the API answers error 'consent_required' with no link; this step records the
// consent (append-only) and refetches, which then returns the link.
function EscrowConsentStep({ offerId, onRecorded }: { offerId: string; onRecorded: () => void }) {
  const [agreed, setAgreed] = useState(false);
  const record = useMutation({
    mutationFn: async () => {
      const consent = await createPurchaseTermsConsent({ bindingEvent: 'offer_deposit', offerId });
      if (!consent.consentId) throw new Error('Purchase terms consent was not recorded.');
    },
    onSuccess: onRecorded,
    onError: (err: Error) => toast.error(err.message ?? 'Could not record consent.'),
  });
  return (
    <div className="rounded border border-teal-200 bg-white p-2" data-testid="escrow-consent">
      <p className="font-medium text-zinc-900">{PURCHASE_TERMS_HEADING}</p>
      <p className="mt-1 text-zinc-600">{PURCHASE_TERMS_BODY}</p>
      <label className="mt-2 flex items-start gap-2">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5"
        />
        <span>{PURCHASE_TERMS_CHECKBOX_LABEL}</span>
      </label>
      <button
        type="button"
        disabled={!agreed || record.isPending}
        onClick={() => record.mutate()}
        className="mt-2 rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50"
      >
        {record.isPending ? '…' : 'Agree and continue to payment'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Buyer — pay the mock escrow deposit on an accepted offer (RUF-570). Shows
// the deposit amount (from the API, SIL-2152) + the org's default payment method before
// confirming. Mock auto-succeeds — no fake pending state.
//
// SIL-1424: a purchase-terms consent step gates the deposit call. The
// "Confirm & pay" action is disabled until the accept checkbox is checked,
// and its mutation POSTs the consent FIRST — the deposit call
// (`offers/:id/pay-deposit`) is never reached without a `consentId` back
// from that POST. A 966 enforcement rejection (PURCHASE_TERMS_CONSENT_REQUIRED,
// mapped by the API to `purchase_terms_consent_required`) resets the
// checkbox and shows an inline message on this step rather than a generic
// toast — see `isPurchaseTermsConsentRequiredError`.
// ---------------------------------------------------------------------------
const BUSINESS_DEPOSIT_NOTICE =
  'Your deposit is paid through Escrow.com. SiliconX sends you the Escrow.com link when the deposit is ready; Escrow.com holds the funds.';

function PayDepositAction({ offer }: { offer: Offer }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.offers() });
  // SIL-2170: only an individual org still needs a mock payment method; a
  // business org has no payment-method UI (Escrow.com panel, SIL-2169). The
  // lookup runs for everyone so a business org with no (legacy) method gets a
  // notice instead of a mock button that always fails.
  const { organizationType } = useAuth();
  const needsMethod = organizationType === 'individual';

  const { data: methods, isError: methodsError } = useQuery<{ data: PaymentMethodDetail[] }>({
    queryKey: qk.paymentMethods(),
    queryFn: async () => {
      const res = await api.api.client.me['payment-methods'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { data: PaymentMethodDetail[] };
    },
    enabled: offer.status === 'accepted',
  });
  const defaultMethod = methods?.data.find((m) => m.isDefault) ?? null;
  // SIL-2152: the deposit is decided in the DB (escrow_rail_setting.deposit_fraction); never computed here.
  const { data: quoteData } = useQuery<{ quote: OfferDepositQuote }>({
    queryKey: [...qk.offers(), offer.id, 'deposit-quote'],
    queryFn: async () => {
      const res = await api.api.client.offers[':id']['deposit-quote'].$get({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { quote: OfferDepositQuote };
    },
    enabled: confirming,
    gcTime: 0,
  });
  const depositAmount = quoteData?.quote.depositUsd ?? null;
  const balanceAmount = quoteData?.quote.balanceUsd ?? null;

  const payDeposit = useMutation({
    mutationFn: async () => {
      // Consent FIRST. Its `consentId` gates the deposit call below — there
      // is no path from this mutation to `pay-deposit` without one.
      const consent = await createPurchaseTermsConsent({
        bindingEvent: 'offer_deposit',
        offerId: offer.id,
      });
      if (!consent.consentId) {
        throw new Error('Purchase terms consent was not recorded.');
      }
      const res = await api.api.client.offers[':id']['pay-deposit'].$post({ param: { id: offer.id } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as PayDepositResponse;
    },
    onSuccess: () => {
      toast.success('Deposit paid - held by Escrow.com.');
      setConfirming(false);
      setAgreedToTerms(false);
      setTermsError(null);
      void invalidate();
    },
    onError: (err: Error) => {
      // 966's PT001 rejection lands here, not in a generic toast — send the
      // buyer back to the terms step rather than an opaque error.
      if (isPurchaseTermsConsentRequiredError(err)) {
        setAgreedToTerms(false);
        setTermsError(PURCHASE_TERMS_CONSENT_ERROR);
        return;
      }
      toast.error(err.message ?? 'Pay deposit failed.');
    },
  });

  if (offer.status !== 'accepted') return null;

  if (!needsMethod && !confirming && !methodsError) {
    if (!methods) return null;
    if (!defaultMethod) {
      return (
        <div className="flex flex-col gap-1">
          <p className="text-xs text-zinc-600" data-testid="deposit-via-escrow-notice">
            {BUSINESS_DEPOSIT_NOTICE}
          </p>
          <EscrowAgentDisclosure />
        </div>
      );
    }
  }

  if (confirming) {
    return (
      <div className="flex flex-col gap-2 rounded border border-teal-300 bg-teal-50 p-2 text-xs">
        <div>
          Deposit: {depositAmount === null ? '…' : <UsdCell value={depositAmount} />} (toward the{' '}
          <UsdCell value={offer.amountUsd} /> price)
        </div>
        <div>
          Balance: {balanceAmount === null ? '…' : <UsdCell value={balanceAmount} />}
        </div>
        {(needsMethod || defaultMethod) && (
          <div>
            Method:{' '}
            {defaultMethod ? defaultMethod.maskedIdentifier : 'No payment method on file'}
          </div>
        )}
        <div className="rounded border border-teal-200 bg-white p-2">
          <p className="font-medium text-zinc-900">{PURCHASE_TERMS_HEADING}</p>
          <p className="mt-1 text-zinc-600">{PURCHASE_TERMS_BODY}</p>
          <label className="mt-2 flex items-start gap-2">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => {
                setAgreedToTerms(e.target.checked);
                setTermsError(null);
              }}
              className="mt-0.5"
            />
            <span>{PURCHASE_TERMS_CHECKBOX_LABEL}</span>
          </label>
          {termsError && <p className="mt-1 text-red-600">{termsError}</p>}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => payDeposit.mutate()}
            disabled={payDeposit.isPending || (needsMethod && !defaultMethod) || !agreedToTerms}
            className="rounded border border-teal-400 px-2 py-1 font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50"
          >
            {payDeposit.isPending ? '…' : 'Confirm & pay (mock)'}
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirming(false);
              setAgreedToTerms(false);
              setTermsError(null);
            }}
            className="rounded border border-zinc-300 px-2 py-1 font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      className="rounded border border-teal-300 px-2 py-1 text-xs font-medium text-teal-700 hover:bg-teal-50"
    >
      Pay deposit (mock)
    </button>
  );
}


/**
 * The buyer's deposit area for one of the viewer's OWN offers: the deposit
 * action while accepted, the Escrow.com balance panel once the deposit is held.
 * Renders nothing for any other status.
 */
export function BuyerDepositArea({ offer }: { offer: Offer }) {
  if (offer.status === 'accepted') return <DepositAction offer={offer} />;
  if (offer.status === 'deposit_held') return <BalanceAction offer={offer} />;
  return null;
}
